import { createMessageDraft, type MessageDraftSession } from "../../lib/messageDraft";
import { clientMoveIn } from "../../lib/clientMoveIn";
import { useTaskWorkflowWritesEnabled } from '../../hooks/useTaskWorkflowWritesEnabled';
import TaskTransitionDialog from './TaskTransitionDialog';
// Ch78 · THE COMPLETE client/lead record, as a modal.
//
// Grant (2026-09-07, from Messages): "Open full client record" must open a
// CLEAN, modern, well-designed MODAL instead of leaving for the Leads tab,
// and "present ALL data we have that could be useful".
//
// What "all" means here, in order of what a locator reaches for:
//   header    who · stage · may-we-text · who owns them · how long we've known them
//   actions   Text / Call / Email — native handoffs, never an in-app send
//   overview  the numbers (budget, move-in, beds, priority) · the profile ·
//             THE LEAD FORM they filled in (every field, incl. the SMS tick
//             and where they came from) · lead intelligence (quality, tier,
//             every verification check) · the intent profile · and a catch-all
//             of every remaining column on the row, so nothing is hidden by
//             a type that forgot it
//   tabs      Timeline · Messages · Shortlist · Files — the drawer's panels,
//             reused, not rebuilt
//
// Honesty rules: a value we do not have renders "—", never blank; a read
// that FAILED says so in its section; the lead form a role cannot read says
// "not visible", never "no form".

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIcon from "@mui/icons-material/Phone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaymentsIcon from "@mui/icons-material/Payments";
import EventIcon from "@mui/icons-material/Event";
import BedIcon from "@mui/icons-material/Bed";
import FlagIcon from "@mui/icons-material/Flag";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { supabase } from "../../lib/supabase";
import { useClientRecord, type ClientRecordRow } from "./useClientRecord";
import { statusConfig } from "./config";
import type { ClientStatus, SentGuestCard, SentListing } from "./types";
import type { Invoice } from "./invoiceTypes";
import { formatDate, formatPhoneNumber, formatRelativeTime } from "./helpers";
import { deriveLeadQuality, deriveLeadSourceLabel, leadIntelTier } from "./leadQuality";
import PersonStat from "./PersonStat";
import PersonPreferences from "./PersonPreferences";
import { buildReadout } from "./verificationReadout";
import VerificationCheckRow from "./VerificationCheckRow";
import IntentProfilePanel from "./IntentProfilePanel";
import LivingProfilePanel from "./LivingProfilePanel";
import ClientTimeline from "./ClientTimeline";
import ClientMessagesPanel from "./ClientMessagesPanel";
import ClientShortlistPanel from "./ClientShortlistPanel";
import ClientFilesPanel from "./ClientFilesPanel";
import EmailPreviewModal from "./EmailPreviewModal";
import InvoicePdfModal from "./InvoicePdfModal";
import { leadTextsOptIn, leadTextsOptInLabel } from "../../lib/leadSmsOptIn";
import { formatLeadMoveInDate } from "../../lib/dashboardLeads";

export interface ClientRecordDialogProps {
  clientId: string | null;
  open: boolean;
  initialTab?: TabId;
  onClose: () => void;
  /** The page already knows consent from the queue row; pass it so the
   * header chip is right on first paint. Falls back to the lead form's tick. */
  consentState?: "granted" | "denied" | "unknown" | null;
  consentSource?: string | null;
  /** The old destination, demoted to a footer link. */
  onOpenInLeads?: (clientId: string) => void;
}

type TabId = "overview" | "timeline" | "messages" | "shortlist" | "files";

const money = (n: number | null | undefined): string =>
  typeof n === "number" && Number.isFinite(n)
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
    : "—";

const text = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const eyebrowSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

/** Label · value pairs in a responsive two-column grid. Missing = "—". */
function Facts({ rows, testId }: { rows: Array<[string, React.ReactNode]>; testId?: string }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        columnGap: 3,
        rowGap: 1.5,
      }}
    >
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ minWidth: 0 }}>
          <Typography sx={{ ...eyebrowSx, fontSize: 11 }}>{label}</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Section({
  title,
  children,
  testId,
  aside,
}: {
  title: string;
  children: React.ReactNode;
  testId?: string;
  aside?: React.ReactNode;
}) {
  return (
    <Box data-testid={testId}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={eyebrowSx}>{title}</Typography>
        {aside}
      </Stack>
      {children}
    </Box>
  );
}

/** Columns the overview already renders by name. Anything else on the row
 * lands in "Everything else" so a column the type forgot is still visible. */
const RENDERED_COLUMNS = new Set([
  "id", "user_id", "first_name", "last_name", "email", "phone", "status", "client_type",
  "priority", "max_budget", "min_bedrooms", "preferred_areas", "notes", "lead_source",
  "move_in_date", "move_in_timeline", "employer", "income_range", "last_contacted_at", "created_at",
  "updated_at", "assigned_agent_id", "origin_lead_id", "sent_list", "assigned_agent",
]);

const ClientRecordDialog: React.FC<ClientRecordDialogProps> = ({
  clientId,
  open,
  onClose,
  consentState,
  consentSource,
  onOpenInLeads,
  initialTab = "overview",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  // Phones: all five tabs in one even-width row, no horizontal scroll (Grant).
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  // Desktop keeps "Timeline · 5" exactly; phones show a small count so all five fit.
  const tabLabel = (label: string, n: number) =>
    phone ? (
      <span>
        {label}
        {n ? <Box component="span" sx={{ ml: 0.5, fontSize: 11, color: "text.secondary", fontWeight: 600 }}>{n}</Box> : null}
      </span>
    ) : `${label}${n ? ` · ${n}` : ""}`;
  const rec = useClientRecord(open ? clientId : null);
  // Keep unsent text when the operator visits another record tab.
  const messageDrafts = React.useRef(new Map<string, MessageDraftSession>());
  const draftFor = (id: string) => {
    let session = messageDrafts.current.get(id);
    if (!session) {
      session = createMessageDraft();
      messageDrafts.current.set(id, session);
    }
    return session;
  };
  const [tab, setTab] = useState<TabId>("overview");
  const taskWorkflowWritesEnabled = useTaskWorkflowWritesEnabled(open ? clientId : null);
  const [taskTransition, setTaskTransition] = React.useState<{ id: string; action: 'complete' | 'cancel' } | null>(null);
  React.useEffect(() => { setTab(initialTab); setTaskTransition(null); }, [clientId, open, initialTab]);
  const [notice, setNotice] = useState<string | null>(null);
  const [emailPreview, setEmailPreview] = useState<{ title: string; html?: string; recipient?: string; sentAt?: string } | null>(null);
  const [invoicePdf, setInvoicePdf] = useState<Invoice | null>(null);

  const c = rec.client ?? null;
  const lead = rec.lead ?? null;
  const name = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unnamed client" : "";
  const stage = c ? statusConfig[c.status as ClientStatus] : undefined;

  // May we text them? The ledger (page-supplied) wins; the form's tick is the
  // fallback story when the ledger has not been asked.
  const texts = useMemo(() => {
    if (consentState === "granted") {
      return { label: consentSource === "website_form" ? "Texts OK · form" : "Texts OK", ok: true };
    }
    if (consentState === "denied") return { label: "No texts (STOP)", ok: false };
    if (lead && leadTextsOptIn(lead) === "yes") return { label: "Texts OK · form", ok: true };
    return null;
  }, [consentState, consentSource, lead]);

  const quality = lead ? deriveLeadQuality(lead) : null;
  const tier = lead ? leadIntelTier(lead) : null;
  const readout = lead ? buildReadout(lead) : [];

  const extraColumns = useMemo(() => {
    if (!c) return [] as Array<[string, string]>;
    return Object.entries(c as ClientRecordRow)
      .filter(([k, v]) => !RENDERED_COLUMNS.has(k) && v !== null && v !== undefined && v !== "")
      .map(([k, v]) => [k.replace(/_/g, " "), text(v)] as [string, string]);
  }, [c]);

  const reloadAfter = async (fn: () => Promise<void>) => {
    try {
      await fn();
      rec.reload();
    } catch (err: any) {
      setNotice(err?.message || "That did not save.");
    }
  };

  const addNote = async (content: string, activityType?: string) => {
    if (!c) return;
    await reloadAfter(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      if (activityType) {
        const { error } = await supabase.from("activities").insert({
          user_id: user.id, entity_type: "client", entity_id: c.id, activity_type: activityType,
          metadata: { client_name: name, note: content },
        });
        if (error) throw error;
        return;
      }
      const { error: noteError } = await supabase
        .from("client_notes")
        .insert({ client_id: c.id, content, created_by: user.id });
      if (noteError) throw noteError;
      await supabase.from("activities").insert({
        user_id: user.id, entity_type: "client", entity_id: c.id, activity_type: "note_added",
        metadata: { client_name: name, note: content.slice(0, 100) },
      });
    });
  };

  const completeTask = (id: string) => setTaskTransition({ id, action: 'complete' });
  const deleteTask = (id: string) => setTaskTransition({ id, action: 'cancel' });


  const counts = {
    timeline: (rec.activities?.length ?? 0) + (rec.notes?.length ?? 0) + (rec.tasks?.length ?? 0),
    shortlist: rec.sentListings?.length ?? 0,
    files: (rec.sentListings?.length ?? 0) + (rec.sentGuestCards?.length ?? 0) + (rec.invoices?.length ?? 0),
  };

  const overview = c && (
    <Stack spacing={3}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1 }}>
        <PersonStat icon={<PaymentsIcon sx={{ fontSize: 15 }} />} label={"Budget"} value={money(c.max_budget)} />
        <PersonStat icon={<EventIcon sx={{ fontSize: 15 }} />} label={"Move-in"} value={text(clientMoveIn(c))} />
        <PersonStat icon={<BedIcon sx={{ fontSize: 15 }} />} label={"Beds"} value={c.min_bedrooms != null ? `${c.min_bedrooms}+` : "—"} />
        <PersonStat icon={<FlagIcon sx={{ fontSize: 15 }} />} label={"Priority"} value={text(c.priority)} />
      </Box>

      {/* GR-16 D2 / GR-17 · what we know, with provenance and decay. Kept
          apart from lead quality (D1) below — the action comes from the pair. */}
      <Section title="What we know about them" testId="record-living-profile">
        <LivingProfilePanel clientId={c.id} onChanged={rec.reload} />
      </Section>

      <Section title="Record" testId="record-profile">
        <Facts
          rows={[
            ["Type", text(c.client_type)],
            ["Stage", stage?.label || text(c.status)],
            ["Lead source", text(c.lead_source)],
            ["Employer", text(c.employer)],
            ["Income range", text(c.income_range)],
            ["Last contacted", c.last_contacted_at ? formatRelativeTime(c.last_contacted_at) : "Never"],
            ["Client since", formatDate(c.created_at)],
            ["Agent", rec.agent === undefined ? "…" : rec.agent?.full_name || rec.agent?.email || "Unassigned"],
            ["Updated", c.updated_at ? formatRelativeTime(c.updated_at) : "—"],
          ]}
        />
        {c.notes && (
          <Box sx={{ mt: 1.25 }}>
            <Typography sx={{ ...eyebrowSx, fontSize: 11 }}>Notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{c.notes}</Typography>
          </Box>
        )}
      </Section>

      <Section
        title="From the lead form"
        testId="record-lead-form"
        aside={lead ? <Typography variant="caption" color="text.secondary">submitted {formatRelativeTime(lead.created_at)}</Typography> : undefined}
      >
        {rec.errors.lead ? (
          <Alert severity="info" data-testid="record-lead-hidden">
            The lead form is not visible for your role.
          </Alert>
        ) : rec.lead === undefined ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : !lead ? (
          <Typography variant="body2" color="text.secondary" data-testid="record-no-lead">
            {c.origin_lead_id ? "The original lead row is gone." : "No lead form on file — entered by hand."}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Facts
              rows={[
                ["Name on form", text(lead.name)],
                ["Email", text(lead.email)],
                ["Phone", lead.phone ? formatPhoneNumber(lead.phone) : "—"],
                ["Texts", leadTextsOptIn(lead) === "unknown" ? "—" : leadTextsOptInLabel(leadTextsOptIn(lead))],
                ["Email list", lead.email_list_opt_in == null ? "—" : lead.email_list_opt_in ? "Opted in" : "Not opted in"],
                ["Budget (as written)", text(lead.budget) === "—" ? money(lead.budget_number) : text(lead.budget)],
                ["Move-in date", lead.move_in_date ? `${formatLeadMoveInDate(lead.move_in_date)} · ${formatDate(lead.move_in_date)}` : "—"],
                ["Apartment size", text(lead.apartment_size)],
                ["Credit", text(lead.credit_status)],
                ["Employment", text(lead.employment_status)],
              ]}
            />
            <PersonPreferences
              neighborhoods={lead.neighborhoods}
              neighborhoodOther={lead.neighborhood_other}
              pets={lead.pets}
            />
            {lead.notes && (
              <Box>
                <Typography sx={{ ...eyebrowSx, fontSize: 11 }}>What they wrote</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{lead.notes}</Typography>
              </Box>
            )}
            <Box>
              <Typography sx={{ ...eyebrowSx, fontSize: 11, mb: 0.5 }}>Where they came from</Typography>
              <Facts
                rows={(
                  [
                    ["Source", deriveLeadSourceLabel(lead)],
                    ["Channel", lead.source_channel],
                    ["Platform", lead.source_platform],
                    ["Referrer", lead.source_referrer_host],
                    ["UTM source", lead.source_utm_source],
                    ["UTM medium", lead.source_utm_medium],
                    ["UTM campaign", lead.source_utm_campaign],
                    ["UTM content", lead.source_utm_content],
                    ["UTM term", lead.source_utm_term],
                    ["Landing page", lead.source_landing_path],
                  ] as Array<[string, string | undefined]>
                )
                  .filter(([, v], i) => i === 0 || (v && v.trim()))
                  .map(([k, v]) => [k, text(v)] as [string, React.ReactNode])}
              />
            </Box>
          </Stack>
        )}
      </Section>

      {lead && quality && (
        <Section
          title="Lead intelligence"
          testId="record-intel"
          aside={
            <Stack direction="row" spacing={0.75}>
              {tier && (
                <Chip
                  size="small"
                  data-testid="record-intel-tier"
                  label={tier.replace("_", " ")}
                  color={tier === "ULTRA_REAL" || tier === "REAL" ? "success" : tier === "UNCERTAIN" ? "default" : "error"}
                  variant="outlined"
                />
              )}
              <Chip
                size="small"
                icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
                label={quality.isVerified ? `${quality.chipLabel} · ${quality.score}` : "Unverified"}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          }
        >
          {readout.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Not verified yet — the checks run when an admin clicks Verify on the lead.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {readout.map((row) => (
                <VerificationCheckRow key={row.id} row={row} />
              ))}
              {lead.verification_checked_at && (
                <Typography variant="caption" color="text.secondary">
                  Checked {formatRelativeTime(lead.verification_checked_at)}
                  {lead.verification_provider ? ` · ${lead.verification_provider}` : ""}
                </Typography>
              )}
            </Stack>
          )}
        </Section>
      )}

      <Section title="Intent profile" testId="record-intent">
        {rec.errors.intentProfile ? (
          <Alert severity="warning">Could not read the intent profile: {rec.errors.intentProfile}</Alert>
        ) : (
          <IntentProfilePanel profile={rec.intentProfile ?? null} />
        )}
      </Section>

      {extraColumns.length > 0 && (
        <Section title="Everything else on the record" testId="record-extra">
          <Facts rows={extraColumns} />
        </Section>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      data-testid="client-record-dialog"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, height: fullScreen ? "100%" : "min(90vh, 900px)", display: "flex" } }}
    >
      {/* Header · name once, X top-right, chips beneath, contact on its own
          lines (never truncated mid-number), verbs as icon buttons (Packet E
          rule 1). Same data as before: stage, texts, email, phone, tenure. */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 16px)", sm: 2.5 },
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {rec.errors.client ? (
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Alert severity="error" data-testid="record-error" sx={{ flex: 1 }}>Could not load this client: {rec.errors.client}</Alert>
            <IconButton onClick={onClose} aria-label="Close client record"><CloseIcon /></IconButton>
          </Stack>
        ) : !c ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700}>{rec.client === null ? "Client not found" : "Loading…"}</Typography>
            <IconButton onClick={onClose} aria-label="Close client record"><CloseIcon /></IconButton>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {/* Grant: "it doesnt need the profile pic" — name, tags and contact
                use the full width. */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box minWidth={0} flex={1}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  data-testid="record-name"
                  sx={{ lineHeight: 1.25, overflowWrap: "anywhere" }}
                >
                  {name}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {stage && <Chip size="small" label={stage.label} sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: alpha(stage.color, 0.16), color: stage.color }} />}
                  {texts && (
                    <Chip
                      size="small"
                      data-testid="record-texts"
                      icon={<SmsIcon sx={{ fontSize: 14 }} />}
                      label={texts.label}
                      color={texts.ok ? "success" : "default"}
                      variant="outlined"
                      sx={{ height: 22, fontSize: 12, fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Box>
              <IconButton onClick={onClose} aria-label="Close client record" sx={{ mt: -0.5, mr: -1, color: "text.secondary" }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box minWidth={0} data-testid="record-contact">
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{c.email || "no email"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.phone ? formatPhoneNumber(c.phone) : "no phone"} · with us {formatRelativeTime(c.created_at)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} data-testid="record-actions">
                <Tooltip title="Text">
                  <span>
                    <IconButton aria-label="Text" data-testid="record-action-text" color="primary" disabled={!c.phone || texts?.ok === false} href={`sms:${c.phone ?? ""}`}
                      sx={{ width: 40, height: 40, bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground" } }}>
                      <SmsIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Call">
                  <span>
                    <IconButton aria-label="Call" disabled={!c.phone} href={`tel:${c.phone ?? ""}`} sx={{ width: 40, height: 40, border: `1px solid ${theme.palette.divider}` }}>
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Email">
                  <span>
                    <IconButton aria-label="Email" disabled={!c.email} href={`mailto:${c.email ?? ""}`} sx={{ width: 40, height: 40, border: `1px solid ${theme.palette.divider}` }}>
                      <MailOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Box>

      {c && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={phone ? "fullWidth" : "scrollable"}
          scrollButtons={phone ? false : "auto"}
          sx={{
            borderBottom: 1, borderColor: "divider", minHeight: 44, px: { xs: 0, sm: 1.5 },
            "& .MuiTab-root": {
              minHeight: 44, minWidth: 0, px: { xs: 0.25, sm: 1.5 },
              fontSize: { xs: 13, sm: 14 }, letterSpacing: { xs: "-0.01em", sm: "normal" },
              textTransform: "none", fontWeight: 600, whiteSpace: "nowrap",
            },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="timeline" label={tabLabel("Timeline", counts.timeline)} />
          <Tab value="messages" label="Messages" />
          <Tab value="shortlist" label={tabLabel("Shortlist", counts.shortlist)} />
          <Tab value="files" label={tabLabel("Files", counts.files)} />
        </Tabs>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 3 }}>
        {taskWorkflowWritesEnabled && taskTransition && rec.tasks?.find(task => task.id === taskTransition.id) && <TaskTransitionDialog
          key={taskTransition.id + taskTransition.action}
          task={rec.tasks.find(task => task.id === taskTransition.id)!} action={taskTransition.action}
          onClose={() => setTaskTransition(null)} onSaved={() => { rec.reload(); }} />}
        {notice && <Alert severity="warning" onClose={() => setNotice(null)} sx={{ mb: 1.5 }}>{notice}</Alert>}
        {c && tab === "overview" && overview}
        {c && tab === "timeline" && (
          <ClientTimeline
            taskWorkflowWritesEnabled={taskWorkflowWritesEnabled}
            activities={rec.activities ?? []}
            notes={rec.notes ?? []}
            tasks={rec.tasks ?? []}
            sentListings={rec.sentListings ?? []}
            savedSearches={rec.savedSearches ?? []}
            sentGuestCards={rec.sentGuestCards ?? []}
            invoices={rec.invoices ?? []}
            isLoading={rec.activities === undefined}
            loadingTasks={rec.tasks === undefined}
            clientWishlist={c.notes}
            onAddNote={addNote}
            onAddTask={() => setNotice("Add tasks from the Leads area — this record is read-mostly.")}
            onCompleteTask={completeTask}
            onDeleteTask={deleteTask}
            onOpenInvoicePdf={(inv) => setInvoicePdf(inv)}
            onOpenListingEmail={(l: SentListing) => setEmailPreview({ title: l.subject || "Sent list", html: l.email_html, recipient: l.recipient_email, sentAt: l.sent_at })}
            onOpenGuestCardEmail={(g: SentGuestCard) => setEmailPreview({ title: "Guest card", html: (g as any).email_html, recipient: (g as any).recipient_email, sentAt: (g as any).sent_at })}
          />
        )}
        {c && tab === "messages" && <ClientMessagesPanel clientId={c.id} clientName={name} draftSession={draftFor(c.id)} />}
        {c && tab === "shortlist" && (
          <ClientShortlistPanel client={c} sentListings={rec.sentListings ?? []} savedSearches={rec.savedSearches ?? []} />
        )}
        {c && tab === "files" && (
          <ClientFilesPanel
            clientId={c.id}
            sentListings={rec.sentListings ?? []}
            sentGuestCards={rec.sentGuestCards ?? []}
            invoices={rec.invoices ?? []}
            isLoading={rec.sentListings === undefined || rec.invoices === undefined}
            onOpenListingEmail={(l) => setEmailPreview({ title: l.subject || "Sent list", html: l.email_html, recipient: l.recipient_email, sentAt: l.sent_at })}
            onOpenGuestCardEmail={(g) => setEmailPreview({ title: "Guest card", html: (g as any).email_html, recipient: (g as any).recipient_email, sentAt: (g as any).sent_at })}
            onOpenInvoicePdf={(inv) => setInvoicePdf(inv)}
          />
        )}
        {Object.keys(rec.errors).filter((k) => !["lead", "client", "intentProfile"].includes(k)).length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="record-partial">
            Some sections could not be read: {Object.keys(rec.errors).filter((k) => !["lead", "client", "intentProfile"].includes(k)).join(", ")}.
          </Alert>
        )}
      </Box>

      {/* One way out: the X in the header (F36). The footer keeps only the
          secondary destination, and only when there is one. */}
      {onOpenInLeads && c && (
        <>
          <Divider />
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)", display: "flex", alignItems: "center" }}>
            <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => onOpenInLeads(c.id)} sx={{ textTransform: "none", fontWeight: 600 }} data-testid="record-open-in-leads">
              Open in Leads
            </Button>
          </Box>
        </>
      )}

      <EmailPreviewModal
        open={emailPreview !== null}
        onClose={() => setEmailPreview(null)}
        title={emailPreview?.title || ""}
        emailHtml={emailPreview?.html}
        metadata={{ recipient: emailPreview?.recipient, sentAt: emailPreview?.sentAt }}
      />
      <InvoicePdfModal open={invoicePdf !== null} onClose={() => setInvoicePdf(null)} invoice={invoicePdf} />
    </Dialog>
  );
};

export default ClientRecordDialog;
